// V8.1 · Purge definitivo (em massa) — separa o que pode ir agora do que exige reversão.
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const GROUPS = [
  { key: 'can_purge_now', label: 'Pode excluir agora', tone: 'text-emerald-700' },
  { key: 'needs_reversal', label: 'Precisa estornar', tone: 'text-amber-700' },
  { key: 'has_nfse', label: 'Tem NFS-e ativa', tone: 'text-rose-700' },
  { key: 'has_remessa', label: 'Em remessa bancária', tone: 'text-rose-700' },
  { key: 'protected', label: 'Protegido', tone: 'text-slate-500' },
];

const money = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function PurgeBulkDialog({ open, titleIds = [], onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || titleIds.length === 0) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post('/finance/entries/purge-bulk', { title_ids: titleIds, dry_run: true });
        setPreview(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail?.message || 'Falha ao calcular impacto');
      } finally { setLoading(false); }
    })();
  }, [open, titleIds]);

  const confirm = async () => {
    if (!reason.trim()) { toast.error('Informe o motivo'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/finance/entries/purge-bulk',
        { title_ids: titleIds, reason, dry_run: false });
      toast.success(`${data.purged} título(s) excluído(s) definitivamente · auditoria preservada`);
      onDone?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Falha no purge');
    } finally { setBusy(false); }
  };

  const canNow = preview?.counts?.can_purge_now || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-auto" data-testid="purge-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="h-5 w-5" /> Excluir definitivamente ({titleIds.length})
          </DialogTitle>
          <DialogDescription>
            Hard-delete operacional. Pagamento já estornado não bloqueia. A trilha de auditoria (TITLE_PURGED) é sempre preservada.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Calculando impacto…</div>}

        {preview && (
          <div className="space-y-3" data-testid="purge-bulk-preview">
            {GROUPS.map((g) => (
              <div key={g.key}>
                <p className={`text-xs font-semibold ${g.tone}`}>
                  {g.label}: {preview.counts?.[g.key] ?? 0}
                </p>
                <div className="mt-1 space-y-0.5">
                  {(preview[g.key] || []).slice(0, 6).map((i) => (
                    <p key={i.title_id} className="text-[11px] text-slate-500">
                      {i.student_name || '—'} · {i.description || '—'} · {i.due_date || '—'} · {money(i.amount)}
                      {i.needs_reversal?.length ? ` — ${i.needs_reversal[0]}` : ''}
                      {i.blocks?.length ? ` — ${i.blocks[0]}` : ''}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Input placeholder="Motivo (obrigatório)" value={reason} onChange={(e) => setReason(e.target.value)}
            className="sm:w-72" data-testid="purge-bulk-reason" />
          <Button variant="outline" onClick={onClose}>Voltar</Button>
          <Button variant="destructive" onClick={confirm} disabled={busy || canNow === 0 || !reason.trim()}
            data-testid="purge-bulk-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Excluir {canNow} definitivamente</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
